import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { tableColumns, ENTITY_TYPES, typeLabel } from './DonationManagementData';
import CreateDonationManagement from './CreateDonationManagement';
import EditDonationManagement from './EditDonationManagement';

import { ServerPath } from '../../../Constant';
import { _axios } from '../../../utils/axios';

import EditIcon from '../../../assets/images/svg/editLearning.svg';
import ShareIcon from '../../../assets/images/svg/publishLearning.svg';
import ArchiveIcon from '../../../assets/images/svg/archiveLearning.svg';
import ArchiveEnabled from '../../../assets/images/svg/archiveEnabled.svg';
import Search from '../../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../../assets/images/svg/search_iconSos.svg';
import Calendar from '../../../assets/images/svg/Calendar_SoS.svg';
import Filter from '../../../assets/images/svg/Filter_SoS.svg';

const tableStyles = `
  .rdt_TableCell { padding: 4px 2px !important; }
  .rdt_TableHead { padding: 4px 2px !important; }
  .rdt_TableCol { padding: 4px 2px !important; }
  .rdt_TableCell[data-column-id="status"] {
    position: sticky !important; right: 140px !important;
    background-color: white !important; z-index: 2 !important;
    display: flex !important; justify-content: center !important; align-items: center !important;
  }
  .rdt_TableHeadRow [data-column-id="status"] {
    position: sticky !important; right: 140px !important;
    background-color: white !important; z-index: 2 !important;
    display: flex !important; justify-content: center !important; align-items: center !important;
    padding-left: 10px !important; padding-right: 0 !important;
  }
  .rdt_TableCell[data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .rdt_TableHeadRow [data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .data-tables { overflow-x: auto; }
  .btn[title]:hover:after {
    content: attr(title);
    position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
    padding: 4px 8px; color: white; border-radius: 4px;
    font-size: 12px; white-space: nowrap; z-index: 10;
  }
  .btn[title] { position: relative; }
  .btn-link:hover { text-decoration: none !important; }
  .btn[disabled] { cursor: not-allowed; }
`;

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 0 },
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
};

const DonationManagementTable = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleDelete, setToggleDelete] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [data, setData] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: '', row: null });

  // Server-side filters
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // Header UI
  const [showSearch, setShowSearch] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const [dateRange, setDateRange] = useState([
    { startDate: null, endDate: null, key: 'selection' }
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setShowSearch(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) setShowTypeDropdown(false);
      if (calendarRef.current && !calendarRef.current.contains(event.target)) setShowCalendar(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildActionCell = (row) => (
    <div className="d-flex justify-content-center gap-2">
      <button
        className="btn btn-link p-0"
        title="Edit"
        onClick={(e) => {
          e.stopPropagation();
          setEditRowData(row);
          setShowEdit(true);
        }}
      >
        <img src={EditIcon} alt="edit" style={{ width: '24px', height: '24px' }} />
      </button>
      <div title={row.is_active ? 'Published' : 'Publish'}>
        <button
          className="btn btn-link p-0"
          disabled={!!row.is_active}
          onClick={(e) => {
            e.stopPropagation();
            if (!row.is_active) {
              setModalConfig({ type: 'publish', row });
              setShowModal(true);
            }
          }}
          style={{ opacity: row.is_active ? 0.5 : 1 }}
        >
          <img src={ShareIcon} alt="publish" style={{ width: '24px', height: '24px' }} />
        </button>
      </div>
      <div title={row.is_active ? 'Archive' : 'Archived'}>
        <button
          className="btn btn-link p-0"
          disabled={!row.is_active}
          onClick={(e) => {
            e.stopPropagation();
            if (row.is_active) {
              setModalConfig({ type: 'archive', row });
              setShowModal(true);
            }
          }}
          style={{ opacity: !row.is_active ? 0.5 : 1 }}
        >
          <img src={!row.is_active ? ArchiveEnabled : ArchiveIcon} alt="archive" style={{ width: '24px', height: '24px' }} />
        </button>
      </div>
    </div>
  );

  const transformRow = (item) => ({
    donation_object_id: item.donation_object_id,
    object_type: item.object_type,
    object_id: item.object_id,
    object_name: item.object_name,
    object_location: item.object_location,
    active_item_count: item.active_item_count ?? 0,
    is_active: item.is_active,
    settlement_mode: item.settlement_mode || 'MYPARIKRAMA',
    type_label: typeLabel(item.object_type),
    updated_date: formatDate(item.updated_at || item.created_at),
    raw_updated_at: item.updated_at || item.created_at,
    status_component: (
      <span
        className="d-inline-block"
        style={{
          minWidth: '70px', textAlign: 'center', borderRadius: '20px',
          padding: '4px 8px',
          backgroundColor: item.is_active ? '#00C853' : '#9CA3AF',
          color: '#FFFFFF', fontSize: '10px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {item.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
    raw_data: item,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { page: 1, limit: 100 };
      if (selectedType !== 'all') params.object_type = selectedType;
      if (selectedFilter !== 'all') params.is_active = selectedFilter;
      if (searchValue && searchValue.trim()) params.search = searchValue.trim();

      const response = await _axios.get(`${ServerPath}/master-donation-objects`, { params });

      if (response.data?.success) {
        const rows = (response.data.rows || []).map(transformRow);
        setRawRows(rows);
        setTotalRows(response.data.total ?? rows.length);
      } else {
        setRawRows([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching donation objects:', error);
      toast.error('Failed to fetch donation list');
      setRawRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, selectedType]);

  // Client-side calendar filter applied to server rows
  useEffect(() => {
    let filtered = [...rawRows];
    const dv = dateRange[0];
    if (dv?.startDate && dv?.endDate) {
      const start = new Date(dv.startDate); start.setHours(0, 0, 0, 0);
      const end = new Date(dv.endDate); end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(r => {
        const d = new Date(r.raw_updated_at);
        return d >= start && d <= end;
      });
    }
    // Re-bind action cell so handlers use latest closures
    setData(filtered.map(r => ({ ...r, action: buildActionCell(r) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRows, dateRange]);

  const handleSearch = (value) => {
    setSearchValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchData();
    }, 350);
  };

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    setShowDropdown(false);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    setShowTypeDropdown(false);
  };

  const handleDateRangeChange = (item) => setDateRange([item.selection]);
  const clearDateFilter = () => {
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setShowCalendar(false);
  };

  const handleRowSelected = useCallback(state => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handleCreateClose = (shouldRefresh = false) => {
    setShowCreate(false);
    if (shouldRefresh) fetchData();
  };
  const handleEditClose = (shouldRefresh = false) => {
    setShowEdit(false);
    setEditRowData(null);
    if (shouldRefresh) fetchData();
  };

  const handleModalConfirm = async () => {
    const row = modalConfig.row;
    if (!row) { setShowModal(false); return; }
    const targetActive = modalConfig.type === 'publish' ? 1 : 0;
    try {
      const res = await _axios.post(`${ServerPath}/master-donation-objects`, {
        object_type: row.object_type,
        object_id: row.object_id,
        is_active: targetActive,
      });
      if (res.data?.success) {
        toast.success(`Donation ${targetActive ? 'published' : 'archived'} successfully`);
        fetchData();
      } else {
        toast.error('Status update failed');
      }
    } catch (err) {
      console.error('Status toggle error:', err);
      toast.error('Operation failed');
    }
    setShowModal(false);
  };

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Container fluid={true}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Donation Management</h3>
          <div className="d-flex align-items-center gap-3">

            {/* Search */}
            <div className="position-relative" ref={searchRef}>
              <button className="bg-transparent border-0" onClick={() => setShowSearch(true)}>
                <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
              </button>
              {showSearch && (
                <div style={{ position: 'absolute', top: '45px', left: '-180px', width: '220px', zIndex: 1000 }}>
                  <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#F8F7FA', borderRadius: '6px', padding: '6px 12px' }}>
                      <input
                        type="text"
                        placeholder="Search Here..."
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '14px', color: '#666', outline: 'none' }}
                        autoFocus
                      />
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => fetchData()}
                      >
                        <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Calendar */}
            <div className="position-relative" ref={calendarRef}>
              <button className="bg-transparent border-0" onClick={() => setShowCalendar(!showCalendar)}>
                <img src={Calendar} alt="calendar" style={{ width: '36px', height: '36px' }} />
              </button>
              {showCalendar && (
                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-3"
                  style={{ width: 'auto', maxWidth: '300px', zIndex: 1000, right: 0 }}>
                  <style>{`
                    .date-range { font-size: 12px !important; }
                    .date-range .rdrMonth { width: 240px !important; padding: 0 !important; }
                    .date-range .rdrCalendarWrapper {
                      font-size: 12px !important; width: 240px !important;
                      border-radius: 8px !important; overflow: hidden !important; background: white !important;
                    }
                    .date-range .rdrDateDisplayWrapper { display: none !important; }
                    .rdrMonthAndYearWrapper { padding-top: 5px !important; height: 40px !important; }
                    .rdrMonth { padding: 0 !important; }
                  `}</style>
                  <DateRange
                    editableDateInputs={true}
                    onChange={handleDateRangeChange}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    maxDate={new Date()}
                    months={1}
                    direction="horizontal"
                    className="date-range"
                    staticRanges={[]}
                    inputRanges={[]}
                    rangeColors={['#0066ff']}
                    minDate={new Date('2000-01-01')}
                    showSelectionPreview={true}
                    showMonthAndYearPickers={true}
                    monthDisplayFormat="MMMM yyyy"
                    dayDisplayFormat="d"
                    showDateDisplay={false}
                    monthsShown={1}
                  />
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={clearDateFilter}>Clear</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Entity Type filter */}
            <div className="position-relative" ref={typeDropdownRef}>
              <button className="bg-transparent border-0" onClick={() => setShowTypeDropdown(!showTypeDropdown)} title="Entity Type">
                <img src={Filter} alt="type" style={{ width: '36px', height: '36px' }} />
              </button>
              {showTypeDropdown && (
                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2"
                  style={{ width: '200px', zIndex: 1000 }}>
                  <Form>
                    <Form.Check
                      type="radio" name="typeOption" id="type-all" label="All Types"
                      checked={selectedType === 'all'}
                      onChange={() => handleTypeChange('all')}
                    />
                    {ENTITY_TYPES.map(t => (
                      <Form.Check
                        key={t.value} type="radio" name="typeOption" id={`type-${t.value}`}
                        label={t.label}
                        checked={selectedType === t.value}
                        onChange={() => handleTypeChange(t.value)}
                      />
                    ))}
                  </Form>
                </div>
              )}
            </div>

            {/* Status filter */}
            <div className="position-relative" ref={dropdownRef}>
              <button className="bg-transparent border-0" onClick={() => setShowDropdown(!showDropdown)} title="Status">
                <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
              </button>
              {showDropdown && (
                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2"
                  style={{ width: '180px', zIndex: 1000 }}>
                  <Form>
                    {FILTER_OPTIONS.map(option => (
                      <Form.Check
                        key={String(option.value)}
                        type="radio" name="filterOption" id={`filter-${option.value}`}
                        label={option.label}
                        checked={selectedFilter === option.value}
                        onChange={() => handleFilterChange(option.value)}
                      />
                    ))}
                  </Form>
                </div>
              )}
            </div>

            <Button variant="primary" onClick={() => setShowCreate(true)} className="bg-primary">
              Create Donation
            </Button>
          </div>
        </div>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <DataTable
                  columns={tableColumns}
                  data={data}
                  selectableRows
                  pagination
                  paginationServer={false}
                  paginationTotalRows={totalRows}
                  onSelectedRowsChange={handleRowSelected}
                  clearSelectedRows={toggleDelete}
                  progressPending={loading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CreateDonationManagement show={showCreate} onHide={handleCreateClose} />
      <EditDonationManagement show={showEdit} onHide={handleEditClose} row={editRowData} />
      <ConfirmationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        title="Are you sure?"
        message={modalConfig.type === 'publish'
          ? "You want to set this donation to 'Active'? It will be visible to users."
          : "You want to archive this donation? It will no longer be visible to users."}
      />
    </Fragment>
  );
};

const ConfirmationModal = ({ show, onHide, onConfirm, title, message }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Body className="text-center p-4">
      <div className="mb-4">
        <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex p-4 mb-3">
          <i className="bi bi-exclamation-lg fs-4 text-warning"></i>
        </div>
        <h4>{title}</h4>
        <p className="text-secondary mb-0">{message}</p>
      </div>
      <div className="d-flex gap-2 justify-content-center">
        <Button variant="primary" className="px-4" onClick={onConfirm}>Yes</Button>
        <Button variant="danger" className="px-4" onClick={onHide}>Cancel</Button>
      </div>
    </Modal.Body>
  </Modal>
);

export default DonationManagementTable;
